import type { ReactNode } from "react";

/**
 * Unified line-icon set for the Mini App · Feather/Lucide style, matching
 * CameraIcon (viewBox 24, stroke=currentColor, width 2, round caps). Replaces
 * the emoji glyphs that rendered inconsistently across devices and read cheap —
 * a cross-border logistics service needs a calm, professional, trustworthy look.
 *
 * `currentColor` lets callers tint via CSS (e.g. nav active state).
 */
export type IconName =
  | "home"
  | "catalog"
  | "warehouse"
  | "orders"
  | "profile"
  | "topup"
  | "withdraw"
  | "coupon"
  | "invite"
  | "shipment"
  | "address"
  | "favorite"
  | "message"
  | "bill"
  | "transfer"
  | "settings"
  | "support"
  | "help"
  | "guide"
  | "clock"
  | "cart"
  | "camera"
  | "plane"
  | "calculator"
  | "search"
  | "link"
  | "bell";

function Svg({ size, children }: { size: number; children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

export function MiniIcon({ name, size = 22 }: { name: IconName; size?: number }) {
  switch (name) {
    case "home":
      return (
        <Svg size={size}>
          <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />
        </Svg>
      );
    case "catalog":
      return (
        <Svg size={size}>
          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <path d="M3 6h18" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </Svg>
      );
    case "warehouse":
      return (
        <Svg size={size}>
          <path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35" />
          <path d="m2 8.35 10-5 10 5" />
          <path d="M10 22V12h4v10" />
        </Svg>
      );
    case "orders":
      return (
        <Svg size={size}>
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <path d="m3.27 6.96 8.73 5.05 8.73-5.05" />
          <path d="M12 22.08V12" />
        </Svg>
      );
    case "profile":
      return (
        <Svg size={size}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </Svg>
      );
    case "topup":
      return (
        <Svg size={size}>
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <path d="M2 10h20" />
        </Svg>
      );
    case "withdraw":
      return (
        <Svg size={size}>
          <path d="M12 19V5" />
          <path d="m5 12 7-7 7 7" />
        </Svg>
      );
    case "coupon":
      return (
        <Svg size={size}>
          <path d="M20 12a2 2 0 0 1 2-2V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v3a2 2 0 0 1 0 4v3a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3a2 2 0 0 1-2-2Z" />
          <path d="M9 5v14" />
        </Svg>
      );
    case "invite":
      return (
        <Svg size={size}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M19 8v6M22 11h-6" />
        </Svg>
      );
    case "shipment":
      return (
        <Svg size={size}>
          <path d="M22 2 11 13" />
          <path d="M22 2 15 22l-4-9-9-4 20-7Z" />
        </Svg>
      );
    case "address":
      return (
        <Svg size={size}>
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
          <circle cx="12" cy="10" r="3" />
        </Svg>
      );
    case "favorite":
      return (
        <Svg size={size}>
          <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8Z" />
        </Svg>
      );
    case "message":
      return (
        <Svg size={size}>
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="m2 6 10 7 10-7" />
        </Svg>
      );
    case "bill":
      return (
        <Svg size={size}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
          <path d="M14 2v6h6" />
          <path d="M8 13h8M8 17h5" />
        </Svg>
      );
    case "transfer":
      return (
        <Svg size={size}>
          <path d="M3 21h18" />
          <path d="M5 21V10M9 21V10M15 21V10M19 21V10" />
          <path d="m3 10 9-6 9 6Z" />
        </Svg>
      );
    case "settings":
      return (
        <Svg size={size}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
        </Svg>
      );
    case "support":
      return (
        <Svg size={size}>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
        </Svg>
      );
    case "help":
      return (
        <Svg size={size}>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
          <path d="M12 17h.01" />
        </Svg>
      );
    case "guide":
      return (
        <Svg size={size}>
          <path d="M4.5 16.5c-1.5 1.3-2 5-2 5s3.7-.5 5-2c.7-.8.7-2 0-2.7a2 2 0 0 0-3 0Z" />
          <path d="m12 15-3-3a22 22 0 0 1 8-10c2.5 0 4.5 2 4.5 4.5a22 22 0 0 1-10 8Z" />
          <path d="M9 12H4s.5-3 2-4 5 0 5 0M12 15v5s3-.5 4-2 0-5 0-5" />
        </Svg>
      );
    case "clock":
      return (
        <Svg size={size}>
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </Svg>
      );
    case "cart":
      return (
        <Svg size={size}>
          <circle cx="9" cy="21" r="1" />
          <circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
        </Svg>
      );
    case "camera":
      return (
        <Svg size={size}>
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </Svg>
      );
    case "plane":
      return (
        <Svg size={size}>
          <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
        </Svg>
      );
    case "calculator":
      return (
        <Svg size={size}>
          <rect x="4" y="2" width="16" height="20" rx="2" />
          <path d="M8 6h8" />
          <path d="M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
          <path d="M16 14v4" />
        </Svg>
      );
    case "search":
      return (
        <Svg size={size}>
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </Svg>
      );
    case "link":
      return (
        <Svg size={size}>
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </Svg>
      );
    case "bell":
      return (
        <Svg size={size}>
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </Svg>
      );
  }
}
